import { getFaqList } from '~/models/Faq'
import { externalTransitionType } from '~/models/Utility'

export type localeType = 'ja' | 'en'
export type structuredDataPageType = 'top' | 'contact'
export type translatorType = (key: string) => string

export const BASE_URL = 'https://amahitotsu.com'
const OGP_IMAGE_URL = `${BASE_URL}/OGP.png`
const GOOGLE_MAP_URL = 'https://www.google.com/maps?ftid=0x356a158d993e1173:0x6be7afc152cba213'
// 施設所在地の緯度経度（GoogleMap埋め込みURLの座標）
const LATITUDE = 33.7836472
const LONGITUDE = 129.6647254

const CHECKIN_TIME = '15:00:00+09:00'
const CHECKOUT_TIME = '10:00:00+09:00'

type localeTextType = {
  ja: string
  en: string
}

const SITE_TITLE: localeTextType = {
  ja: '壱岐のグランピング【天比登都】｜自然と心を結ぶトレーラーステイ',
  en: 'Glamping in Iki [Amahitotsu] | A Trailer Stay That Connects You with Nature',
}

const SITE_DESCRIPTION: localeTextType = {
  ja: '博多から高速船で約60分、長崎壱岐島のグランピング。自然に包まれるトレーラーステイで、心ほどける癒やしのひとときを。',
  en: 'Just 60 minutes by high-speed ferry from Hakata, this glamping experience is located on Iki Island in Nagasaki. Enjoy a relaxing, rejuvenating stay in a trailer nestled in nature.',
}

const ADDRESS_REGION: localeTextType = { ja: '長崎県', en: 'Nagasaki' }
const ADDRESS_LOCALITY: localeTextType = { ja: '壱岐市', en: 'Iki City' }
const ADDRESS_STREET: localeTextType = {
  ja: '郷ノ浦町有安触993',
  en: '993 Ariyasufure, Gonoura-cho',
}

export const getSiteTitle = (locale: localeType) => SITE_TITLE[locale]
export const getSiteDescription = (locale: localeType) => SITE_DESCRIPTION[locale]

const normalizeHyphen = (text: string) => text.replace(/[‐-―−]/g, '-')

/** 電話番号を国際表記に統一 */
const toInternationalPhoneNumber = (text: string) => {
  const numbers = text.replace(/[^\d+]/g, '')
  if (numbers.startsWith('+')) {
    return numbers
  }
  return `+81${numbers.replace(/^0/, '')}`
}

/** 言語ごとのサイトルート */
const getLocaleRoot = (locale: localeType) =>
  locale === 'en' ? `${BASE_URL}/en` : BASE_URL

/** 宿泊施設情報（施設名・住所・電話・地図・予約導線） */
const getLodgingBusiness = (locale: localeType, t: translatorType) => {
  const localeRoot = getLocaleRoot(locale)
  return {
    '@type': ['LodgingBusiness', 'Campground'],
    '@id': `${localeRoot}/#lodging`,
    title: `${t('util.site_name')}`,
    name: `${t('util.title')} ${normalizeHyphen(t('util.sub_title'))}`,
    description: SITE_DESCRIPTION[locale],
    url: `${localeRoot}/`,
    image: OGP_IMAGE_URL,
    telephone: toInternationalPhoneNumber(t('util.telephone_address')),
    address: {
      '@type': 'PostalAddress',
      postalCode: normalizeHyphen(t('util.post_code')),
      addressCountry: 'JP',
      addressRegion: ADDRESS_REGION[locale],
      addressLocality: ADDRESS_LOCALITY[locale],
      streetAddress: ADDRESS_STREET[locale],
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: LATITUDE,
      longitude: LONGITUDE,
    },
    hasMap: GOOGLE_MAP_URL,
    sameAs: [t('sns.instagram.url')],
    checkinTime: CHECKIN_TIME,
    checkoutTime: CHECKOUT_TIME,
    potentialAction: {
      '@type': 'ReserveAction',
      target: externalTransitionType.reserve[locale],
    },
  }
}

const getFaqPage = (locale: localeType) => ({
  '@type': 'FAQPage',
  mainEntity: getFaqList(locale).map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer.replace(/\n/g, ' '),
    },
  })),
})

/** ページごとの構造化データ */
export const getStructuredData = (
  locale: localeType,
  t: translatorType,
  pageType: structuredDataPageType,
) => ({
  '@context': 'https://schema.org',
  '@graph':
    pageType === 'top'
      ? [getLodgingBusiness(locale, t), getFaqPage(locale)]
      : [getLodgingBusiness(locale, t)],
})
